import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, OrganisationWithRole } from '@avanzu/types';
import { apiClient } from '../services/apiClient';

// Simplified state shape
interface AuthState {
  user: User | null;
  organisations: OrganisationWithRole[];
  currentOrganisation: OrganisationWithRole | null;
  organisationId: string | null;
  loading: boolean;
}

enum AuthActionTypes {
  SET_LOADING = 'SET_LOADING',
  SET_AUTH_DATA = 'SET_AUTH_DATA',
  SET_CURRENT_ORGANISATION = 'SET_CURRENT_ORGANISATION',
  CLEAR_AUTH = 'CLEAR_AUTH',
  UPDATE_USER = 'UPDATE_USER',
}

// Action types
type AuthAction =
  | { type: AuthActionTypes.SET_LOADING; payload: boolean }
  | {
      type: AuthActionTypes.SET_AUTH_DATA;
      payload: {
        user: User;
        organisations: OrganisationWithRole[];
        organisationId?: string | null;
      };
    }
  | { type: AuthActionTypes.SET_CURRENT_ORGANISATION; payload: string }
  | { type: AuthActionTypes.CLEAR_AUTH }
  | { type: AuthActionTypes.UPDATE_USER; payload: Partial<User> };

// Reducer for cleaner state management
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case AuthActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };

    case AuthActionTypes.SET_AUTH_DATA: {
      const { user, organisations, organisationId } = action.payload;

      // Determine current organisation
      const targetOrgId =
        organisationId || user.currentOrganisationId || organisations[0]?.id || null;
      const currentOrganisation = organisations.find((org) => org.id === targetOrgId) || null;

      return {
        ...state,
        user,
        organisations,
        organisationId: targetOrgId,
        currentOrganisation,
        loading: false,
      };
    }

    case AuthActionTypes.SET_CURRENT_ORGANISATION: {
      const organisationId = action.payload;
      const currentOrganisation =
        state.organisations.find((org) => org.id === organisationId) || null;

      return {
        ...state,
        organisationId,
        currentOrganisation,
      };
    }

    case AuthActionTypes.UPDATE_USER:
      if (!state.user) return state;
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    case AuthActionTypes.CLEAR_AUTH:
      return {
        user: null,
        organisations: [],
        currentOrganisation: null,
        organisationId: null,
        loading: false,
      };

    default:
      return state;
  }
}

// Storage utilities
const storage = {
  save(user: User, organisations: OrganisationWithRole[], organisationId: string | null) {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('organisations', JSON.stringify(organisations));
    if (organisationId) {
      localStorage.setItem('organisationId', organisationId);
    }
  },

  load(): {
    user: User;
    organisations: OrganisationWithRole[];
    organisationId: string | null;
  } | null {
    try {
      const user = localStorage.getItem('user');
      const organisations = localStorage.getItem('organisations');
      const organisationId = localStorage.getItem('organisationId');

      if (!user) return null;

      return {
        user: JSON.parse(user),
        organisations: organisations ? JSON.parse(organisations) : [],
        organisationId: organisationId || null,
      };
    } catch {
      return null;
    }
  },

  clear() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('organisations');
    localStorage.removeItem('organisationId');
    sessionStorage.clear();
  },
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  organisations: OrganisationWithRole[];
  currentOrganisation: OrganisationWithRole | null;
  organisationId: string | null;
  login: (token: string, user?: User, organisations?: OrganisationWithRole[]) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserOrganisationState: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    organisations: [],
    currentOrganisation: null,
    organisationId: null,
    loading: true,
  });

  // Handle auth failures
  const handleAuthFailure = () => {
    storage.clear();
    dispatch({ type: AuthActionTypes.CLEAR_AUTH });
    window.location.href = '/login';
  };

  // Set up API client error handling
  useEffect(() => {
    apiClient.setAuthCallbacks({
      onAuthFailure: handleAuthFailure,
      onOrganisationInvalidated: () => refreshUserOrganisationState(),
    });
  }, []);

  // Load initial auth state — verify the session with the server before
  // trusting the cached user, so we don't flash the app for a stale token.
  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      dispatch({ type: AuthActionTypes.SET_LOADING, payload: false });
      return;
    }

    const stored = storage.load();

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/user/profile`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (cancelled) return;

        if (!response.ok) {
          storage.clear();
          dispatch({ type: AuthActionTypes.CLEAR_AUTH });
          return;
        }

        const profileData = await response.json();
        if (cancelled) return;

        const organisationId =
          profileData.user.currentOrganisationId ||
          stored?.organisationId ||
          profileData.organisations[0]?.id ||
          null;

        storage.save(profileData.user, profileData.organisations, organisationId);
        dispatch({
          type: AuthActionTypes.SET_AUTH_DATA,
          payload: {
            user: profileData.user,
            organisations: profileData.organisations,
            organisationId,
          },
        });
      } catch {
        if (cancelled) return;
        storage.clear();
        dispatch({ type: AuthActionTypes.CLEAR_AUTH });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (token: string, user?: User, organisations?: OrganisationWithRole[]) => {
    localStorage.setItem('token', token);

    if (user && organisations) {
      const organisationId = user.currentOrganisationId || organisations[0]?.id || null;
      storage.save(user, organisations, organisationId);
      dispatch({
        type: AuthActionTypes.SET_AUTH_DATA,
        payload: { user, organisations, organisationId },
      });
    } else {
      // Just token provided - fetch user data
      await refreshUserOrganisationState();
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      storage.clear();
      dispatch({ type: AuthActionTypes.CLEAR_AUTH });
    }
  };

  const refreshUserOrganisationState = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const profileResponse = await apiClient.get('/user/profile');
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();

        const organisationId = profileData.user.currentOrganisationId
          ? profileData.user.currentOrganisationId
          : state.organisationId;

        storage.save(profileData.user, profileData.organisations, organisationId);
        dispatch({
          type: AuthActionTypes.SET_AUTH_DATA,
          payload: {
            user: profileData.user,
            organisations: profileData.organisations,
            organisationId,
          },
        });
      }
    } catch (error) {
      console.error('Failed to refresh user/organisation state:', error);
      // Error handling is done by apiClient interceptor
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        loading: state.loading,
        organisations: state.organisations,
        currentOrganisation: state.currentOrganisation,
        organisationId: state.organisationId,
        login,
        logout,
        refreshUserOrganisationState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
